import { compose, get, keyBy, keys } from 'lodash/fp';
import { createSelector } from 'reselect';
import { City } from '@mews-commander/data-access';
import { SearchType } from '@mews-commander/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { createReducer, createAsyncMetaSelectors, AsyncMetaState } from '@mews/framework';
import { getStringProp } from '@mews/fp';
import { createFetchAction } from '@mews-commander/utils';
import moduleSelector from '../selector';
import { META_STATE_FAILED, META_STATE_STARTED, META_STATE_SUCCEEDED } from '../constants';
import { mapSearchResults } from './utils';
import { CITIES_FETCH, CITIES_FETCH_STARTED, CITIES_FETCH_SUCCEEDED, CITIES_FETCH_FAILED } from './actionTypes';

interface State {
    data: Record<string, City>;
    meta: AsyncMetaState;
}

export const NAME = 'cities';

const FETCH_CITIES_URL = '/cities/getAllByIds';

// duplicated from modules\modules\src\modules\search\actions\actionTypes.ts
// reason: to isolate entities module
const SEARCH_PREFIX = 'search';
const SEARCH_SUCCEEDED = `${SEARCH_PREFIX}/SEARCH_SUCCEEDED`;
const SEARCH_MORE_SUCCEEDED = `${SEARCH_PREFIX}/SEARCH_MORE_SUCCEEDED`;

const mapCitySearch = compose(keyBy('id'), mapSearchResults(SearchType.City));

export const fetchCities =
    ({ cityIds }: { cityIds: string[] }): CommanderThunkAction =>
    async (dispatch, getState, { container }) => {
        const promise = container().apiComponent.request(FETCH_CITIES_URL, { cityIds });

        return dispatch(createFetchAction(CITIES_FETCH, promise));
    };

export const citiesReducer = createReducer(
    { ...META_STATE_SUCCEEDED },
    {
        [CITIES_FETCH_STARTED]: ({ data }: State) => ({
            data,
            ...META_STATE_STARTED,
        }),
        [CITIES_FETCH_SUCCEEDED]: ({ data }: State, cities: Array<City>) => ({
            data: {
                ...data,
                ...keyBy('id', cities),
            },
            ...META_STATE_SUCCEEDED,
        }),
        [CITIES_FETCH_FAILED]: ({ data }: State) => ({
            data,
            ...META_STATE_FAILED,
        }),
        // @ts-expect-error
        [SEARCH_SUCCEEDED]: (state: State, payload) => ({
            ...state,
            ...mapCitySearch(payload),
        }),
        // @ts-expect-error
        [SEARCH_MORE_SUCCEEDED]: (state: State, payload) => ({
            ...state,
            ...mapCitySearch(payload),
        }),
    }
);

const getCitiesEntities = createSelector(moduleSelector, (state) => state[NAME] || {});
export const getCities = compose(get('data'), getCitiesEntities);
export const getCityIds = compose(keys, getCities);
export const getCitiesMeta = compose(get('meta'), getCitiesEntities);

export const getCity = createSelector(getCities, getStringProp('cityId'), (cities, cityId) => cities[cityId] || {});

export const [areCitiesFetching] = createAsyncMetaSelectors(getCitiesMeta);
export const getCityName = createSelector(getCity, (c) => c.name);
